import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { Paginated, SectionListItem } from "../shared/schemas";
import { ErrorState, LoadingState, PageHeader } from "../components/ui";
import { formatMinutes } from "../lib/domain";

const columnHelper = createColumnHelper<SectionListItem>();

export function SectionsPage() {
  const { snapshotTermId, departmentId } = useAppContext();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState("");
  const [selected, setSelected] = useState<SectionListItem | null>(null);
  const query = useQuery({
    queryKey: ["sections", snapshotTermId, departmentId, page, search, method],
    queryFn: () =>
      apiFetch<Paginated<SectionListItem>>(
        `/sections${queryString({
          snapshotTermId,
          page,
          pageSize: 25,
          search,
          department: departmentId,
          method,
        })}`,
      ),
    enabled: Boolean(snapshotTermId),
    placeholderData: (previous) => previous,
  });
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.subject} ${row.courseNumber}`, {
        id: "course",
        header: "Course",
        cell: (info) => <span className="font-bold text-slate-900">{info.getValue()}</span>,
      }),
      columnHelper.accessor("sectionCode", { header: "Section" }),
      columnHelper.accessor("crn", { header: "CRN" }),
      columnHelper.accessor("departmentCode", { header: "Dept" }),
      columnHelper.accessor("actualEnrollment", { header: "Act" }),
      columnHelper.accessor("maxEnrollment", { header: "Max" }),
      columnHelper.accessor("seatsAvailable", {
        header: "Seats",
        cell: (info) => (
          <span className={info.getValue() <= 0 ? "font-bold text-rose-600" : ""}>
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor("instructionMethod", { header: "Method" }),
      columnHelper.accessor(
        (row) => row.meetings.map((meeting) => meeting.room).filter(Boolean).join(", ") || "Online",
        { id: "room", header: "Room" },
      ),
      columnHelper.accessor("facultyName", { header: "Faculty" }),
    ],
    [],
  );
  const table = useReactTable({
    data: query.data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (query.isLoading && !query.data) return <LoadingState />;
  if (query.error) return <ErrorState error={query.error} />;

  return (
    <>
      <PageHeader title="Sections Explorer" description="Search and inspect normalized sections from the selected immutable snapshot." />
      <div className="panel p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_180px]">
          <label className="relative">
            <span className="sr-only">Search sections</span>
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input className="field pl-9" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Course, title, instructor, or CRN" />
          </label>
          <select className="field" value={method} onChange={(event) => { setMethod(event.target.value); setPage(1); }}>
            <option value="">All methods</option>
            <option value="CLS">CLS · In-class</option>
            <option value="HYW">HYW · Hybrid</option>
            <option value="WEB">WEB · Online</option>
            <option value="OFF">OFF · Offsite</option>
          </select>
        </div>
      </div>
      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className="px-4 py-3 font-bold">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="cursor-pointer transition hover:bg-brand-50/50" onClick={() => setSelected(row.original)}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <p className="text-xs text-slate-500">
            {query.data?.total.toLocaleString() ?? 0} sections · Page {query.data?.page ?? 1} of {query.data?.pageCount || 1}
          </p>
          <div className="flex gap-2">
            <button className="button-secondary px-3" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></button>
            <button className="button-secondary px-3" disabled={page >= (query.data?.pageCount ?? 1)} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true">
          <div className="panel max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="badge bg-brand-100 text-brand-700">CRN {selected.crn}</span>
                <h2 className="mt-3 text-xl font-black text-slate-950">{selected.subject} {selected.courseNumber}-{selected.sectionCode}</h2>
                <p className="text-sm text-slate-500">{selected.sectionTitle}</p>
              </div>
              <button className="button-secondary" onClick={() => setSelected(null)}>Close</button>
            </div>
            <dl className="mt-6 grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-3">
              <div><dt className="text-xs font-bold uppercase text-slate-400">Enrollment</dt><dd className="mt-1 font-bold">{selected.actualEnrollment} / {selected.maxEnrollment}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-400">Waitlist</dt><dd className="mt-1 font-bold">{selected.waitlistEnrollment} / {selected.waitlistMax}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-400">Room Capacity</dt><dd className="mt-1 font-bold">{selected.roomCapacity || "N/A"}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-400">Department</dt><dd className="mt-1 font-bold">{selected.departmentCode}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-400">Method</dt><dd className="mt-1 font-bold">{selected.instructionMethod || "N/A"}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-400">Instructor</dt><dd className="mt-1 font-bold">{selected.facultyName}</dd></div>
            </dl>
            <div className="mt-5">
              <h3 className="text-sm font-bold text-slate-900">Meeting Pattern</h3>
              <div className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200">
                {selected.meetings.map((meeting) => (
                  <div key={meeting.id} className="flex justify-between p-3 text-sm">
                    <span className="font-semibold">{meeting.dayCode} · {formatMinutes(meeting.startMinutes)}–{formatMinutes(meeting.endMinutes)}</span>
                    <span className="text-slate-500">{meeting.campus} / {meeting.room}</span>
                  </div>
                ))}
                {selected.meetings.length === 0 && <p className="p-4 text-sm text-slate-400">Asynchronous or no meeting pattern.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
